import { runAiSummaryField } from '@databases/database-ai-fields/run-ai-summary-field';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { getService } from '@databases/service-container';
import { type AiSummaryFieldConfig, DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { useDatabaseAiConfigModal } from './database-ai-config-modal-context.ts';

export const useEditAiSummaryField = ({
  contentId,
  currentViewId,
  fieldId
}: {
  contentId: string;
  currentViewId: string;
  fieldId: string;
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { locale } = useIntl();
  const { openAiConfigModal } = useDatabaseAiConfigModal();

  return useCallback(() => {
    const field = getService('DatabaseSdk').fields.getFieldById(fieldId);

    if (field?.type !== DatabaseFieldType.AI_SUMMARY) {
      return;
    }

    const persistConfig = (config: AiSummaryFieldConfig) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_AI_FIELD_CONFIG,
          fieldId,
          aiFieldConfig: {
            aiFieldType: 'summary',
            field: config
          }
        })
      );
    };
    const initialConfig =
      field.settings.aiFieldConfig?.aiFieldType === 'summary'
        ? field.settings.aiFieldConfig.field
        : undefined;

    openAiConfigModal({
      type: 'ai-summary-field',
      props: {
        fieldId,
        fieldName: field.name,
        mode: 'edit',
        openedFrom: 'editFieldDropdown',
        initialConfig,
        onSaveAndRun: (config, abortSignal) => {
          persistConfig(config);
          return runAiSummaryField({
            abortSignal,
            contentId,
            fieldId,
            targetFieldName: field.name,
            config,
            locale,
            viewId: currentViewId,
            operation: 'refresh-entire-column',
            triggerType: 'saveAndRun',
            runScope: 'entireColumn',
            source: 'aiFieldModal'
          });
        },
        onSaveWithoutRunning: persistConfig
      }
    });
  }, [contentId, currentViewId, dispatch, fieldId, locale, openAiConfigModal]);
};
